import { replace } from '@lagunovsky/redux-react-router'
import { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import mem from 'mem'

import { getAuthToken, getRefreshToken, getUser } from './auth'
import { createSession, destroySession } from './session'
import { clear } from '../../app-reducer'
import { xFestivalName } from '../../core/constants/baseUrls'
import { http, httpPublic } from '../../core/services/http'
import store from '../../store'
import { resetLogin } from '../ducks/login'

/* eslint-disable no-param-reassign */
const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const authToken = getAuthToken()

  if (authToken && config.headers) {
    config.headers.Authorization = authToken
  }

  if (xFestivalName.value) {
    config.headers['x-festival-name'] = xFestivalName.value
  } else {
    const url = new URL(document.URL)
    const pathnames = url.pathname.split('/')
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

    for (const pathname of pathnames.reverse()) {
      if (!pathname.match(uuidRegex)) {
        config.headers['x-festival-name'] = pathname
        break
      }
    }
  }

  return config
}

const refreshToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  const user = getUser()
  try {
    const response = await httpPublic.post('/operations/auth/refresh-token', {
      refreshToken: refreshToken,
    })
    createSession(
      `${response.data.token_type} ${response.data.access_token}`,
      response.data.refresh_token,
      user,
      { value: 'en-US', label: 'English' },
    )
    return `${response.data.token_type} ${response.data.access_token}`
  } catch (e) {
    destroySession()
    store.dispatch(clear())
    store.dispatch(resetLogin())
    replace('/account/logout')
    return null
  }
}

const maxAge = 1
const memoizedRefreshToken = mem(refreshToken, { maxAge })

const requestErrorInterceptor = (error: AxiosError): Promise<AxiosError> =>
  Promise.reject(error)

const responseInterceptor = (response: AxiosResponse): AxiosResponse => response

const responseErrorInterceptor = async (
  error: AxiosError,
): Promise<AxiosError> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = error.config as any
  if (error?.response?.status === 401 && !config?._retry) {
    config._retry = true
    const accessToken = await memoizedRefreshToken()
    if (accessToken) {
      config.headers = {
        ...config.headers,
        authorization: accessToken,
      }
      return http(config)
    }
    // Session already destroyed and redirected in refreshToken function
    return Promise.reject(error)
  }

  return Promise.reject(error)
}

const registerAuthInterceptor = (): void => {
  http.interceptors.request.use(requestInterceptor, requestErrorInterceptor)
  http.interceptors.response.use(responseInterceptor, responseErrorInterceptor)
}

export default registerAuthInterceptor
